import { IDENTITY_ERROR_CODES, isIdentityApiError, isMagicLinkError } from '@identity/application/errors';

/** What a screen needs to show an inline error: one i18n key, and the wait when there is one. */
export type IdentityErrorMessage = {
  i18nKey: string;
  /** Seconds to wait, only on a rate limited failure. The key with `{{wait}}` interpolates it. */
  retryAfterSeconds?: number;
};

/** i18n prefix of the inline error texts. */
const ERROR_KEY_PREFIX = 'errors';

/** Suffix of the variant that names the wait. */
const WAIT_KEY_SUFFIX = '_WAIT';

/** Key used when the error has no code with a text in this build. */
export const UNKNOWN_ERROR_KEY = `${ERROR_KEY_PREFIX}.UNKNOWN`;

/**
 * The codes that have a variant naming the wait. Any other code ignores a wait even if one arrives:
 * a key that does not exist renders as its own raw name on the screen.
 */
const CODES_WITH_WAIT_COPY: readonly string[] = [
  IDENTITY_ERROR_CODES.LINK_SEND_RATE_LIMITED,
  IDENTITY_ERROR_CODES.RECOVERY_RATE_LIMITED,
];

/**
 * The i18n key of one error code, so a screen that needs to tell one code apart from the rest
 * compares against a key built the same way instead of writing the prefix out by hand.
 */
export const identityErrorKey = (code: string): string => `${ERROR_KEY_PREFIX}.${code}`;

/**
 * Converts an error thrown by a use case into the text a step shows under its field or button.
 *
 * It reads only the domain codes, never the transport. An error without one — a lost connection, a
 * 5xx — gets the generic key: the user can react to "we could not connect", not to a number.
 */
export const resolveIdentityErrorMessage = (error: unknown): IdentityErrorMessage => {
  if (isMagicLinkError(error)) {
    return { i18nKey: identityErrorKey(error.code) };
  }

  if (isIdentityApiError(error) && error.code) {
    const { code, retryAfterSeconds } = error;
    const hasWaitCopy = typeof retryAfterSeconds === 'number' && CODES_WITH_WAIT_COPY.includes(code);

    return {
      i18nKey: identityErrorKey(hasWaitCopy ? `${code}${WAIT_KEY_SUFFIX}` : code),
      retryAfterSeconds,
    };
  }

  return { i18nKey: UNKNOWN_ERROR_KEY };
};
